import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn } from 'lucide-react';

/**
 * LogoZoomPopover — Feb 2026.
 *
 * Renders a fixed-position image preview near the click point, sized
 * up to ~280px so a tiny header icon becomes actually readable. Stays
 * pinned near the original mouse cursor (clamped to the viewport so
 * it never bleeds off-screen) and dismisses on:
 *   • backdrop click
 *   • ESC keypress
 *   • the X button on the card
 *
 * Used by CompanyIcon (header) and CompanyLogo (Fund Overview) when
 * the `zoomable` prop is set.
 */
export default function LogoZoomPopover({ src, anchor, ticker, onClose }) {
  // Clamp the popover to the viewport. We want the top-left ~16px
  // from the anchor click, but if that pushes it past the right/
  // bottom edge we slide it back.
  const POPOVER_W = 280;
  const POPOVER_H = 280;
  const margin = 12;
  const [pos] = useState(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    let left = (anchor?.x ?? vw / 2) + 16;
    let top = (anchor?.y ?? vh / 2) + 16;
    if (left + POPOVER_W + margin > vw) left = Math.max(margin, vw - POPOVER_W - margin);
    if (top + POPOVER_H + margin > vh) top = Math.max(margin, vh - POPOVER_H - margin);
    return { left, top };
  });

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] bg-black/40 backdrop-blur-[1px]"
      onClick={close}
      data-testid="logo-zoom-backdrop"
    >
      <div
        className="absolute bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        style={{ left: pos.left, top: pos.top, width: POPOVER_W, height: POPOVER_H }}
        onClick={(e) => e.stopPropagation()}
        data-testid="logo-zoom-popover"
      >
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/60">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
            <ZoomIn className="w-3 h-3" />
            {ticker || 'Logo'}
          </div>
          <button
            type="button"
            onClick={close}
            className="p-0.5 rounded hover:bg-slate-200/70 dark:hover:bg-slate-700/60 text-slate-500 dark:text-slate-400"
            data-testid="logo-zoom-close"
            aria-label="Close zoom"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center justify-center p-6 bg-white dark:bg-white/95" style={{ height: POPOVER_H - 32 }}>
          <img
            src={src}
            alt={ticker ? `${ticker} logo (enlarged)` : 'Logo (enlarged)'}
            className="max-w-full max-h-full object-contain"
            data-testid="logo-zoom-image"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
